import { API_ENDPOINTS } from '@tsgecart/config';
import { adminApiFetch } from '../../api/admin-client.js';
import type {
  AdjustStockInput,
  AdminBalance,
  AdminMovement,
  AdminWarehouse,
  BalancesListParams,
  MovementsListParams,
} from '../types/inventory.js';

export interface FetchOptions {
  token: string;
  signal?: AbortSignal;
}

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export async function fetchAdminBalances(
  params: BalancesListParams,
  options: FetchOptions,
): Promise<AdminBalance[]> {
  return adminApiFetch<AdminBalance[]>(`${API_ENDPOINTS.adminInventory}${buildQuery({ ...params })}`, options);
}

export async function fetchAdminMovements(
  params: MovementsListParams,
  options: FetchOptions,
): Promise<AdminMovement[]> {
  return adminApiFetch<AdminMovement[]>(
    `${API_ENDPOINTS.adminInventory}/movements${buildQuery({ ...params })}`,
    options,
  );
}

export async function adjustAdminStock(input: AdjustStockInput, options: FetchOptions): Promise<unknown> {
  return adminApiFetch(`${API_ENDPOINTS.adminInventory}/adjustments`, {
    ...options,
    method: 'POST',
    body: input,
  });
}

export async function fetchAdminWarehouses(
  params: { isActive?: boolean; limit?: number; offset?: number },
  options: FetchOptions,
): Promise<AdminWarehouse[]> {
  return adminApiFetch<AdminWarehouse[]>(
    `${API_ENDPOINTS.adminWarehouses}${buildQuery({ ...params })}`,
    options,
  );
}

export interface CreateWarehouseInput {
  code: string;
  name: string;
  addressLine1?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  isActive?: boolean;
  isDefault?: boolean;
}

export async function createAdminWarehouse(
  input: CreateWarehouseInput,
  options: FetchOptions,
): Promise<AdminWarehouse> {
  return adminApiFetch<AdminWarehouse>(API_ENDPOINTS.adminWarehouses, {
    ...options,
    method: 'POST',
    body: input,
  });
}

export type UpdateWarehouseInput = Partial<CreateWarehouseInput>;

export async function updateAdminWarehouse(
  id: string,
  input: UpdateWarehouseInput,
  options: FetchOptions,
): Promise<AdminWarehouse> {
  return adminApiFetch<AdminWarehouse>(`${API_ENDPOINTS.adminWarehouses}/${id}`, {
    ...options,
    method: 'PATCH',
    body: input,
  });
}
